import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import messageHandler from 'utils/messageHandler';
const apiKey = process.env.REACT_APP_MAP_KEY;

const extractAddress = place => {
  const address = {
    city: '',
    state: '',
    zip: '',
    country: '',
    plain() {
      const city = this.city ? this.city + ', ' : '';
      const zip = this.zip ? this.zip + ', ' : '';
      const state = this.state ? this.state + ', ' : '';
      return city + zip + state + this.country;
    }
  };

  if (!Array.isArray(place?.address_components)) {
    return address;
  }

  place.address_components.forEach(component => {
    const types = component.types;
    const value = component.long_name;

    if (types.includes('locality')) {
      address.city = value;
    }

    if (types.includes('administrative_area_level_2')) {
      address.state = value;
    }

    if (types.includes('postal_code')) {
      address.zip = value;
    }

    if (types.includes('country')) {
      address.country = value;
    }
  });

  return address;
};

const getNeighborhoods = (neighborhoodName, neighborhoods) => {
  const neighborhoodsIds = [];

  const parentNeighborhood = neighborhoods.find(barrio => {
    return (
      barrio.label === neighborhoodName ||
      barrio.children?.some(child => neighborhoodName === child.label)
    );
  });

  if (parentNeighborhood) {
    neighborhoodsIds.push(parentNeighborhood.value);

    if (parentNeighborhood.children) {
      parentNeighborhood.children.forEach(child => {
        if (neighborhoodName === child.label) {
          neighborhoodsIds.push(child.value);
        }
      });
    }
  }

  return neighborhoodsIds;
};

const InputAddressAutocomplete = ({
  setMobileGoogle,
  ready,
  values,
  setValues,
  setEmptyInput,
  neighborhoods
}) => {
  const searchInput = useRef(null);
  const inputValue =
    values.lat && `${values.street} ${values.number}, Buenos Aires, Argentina`;

  const onChangeAddress = autocomplete => {
    const place = autocomplete.getPlace();
    if (
      place?.address_components?.some(ac => ac.types.includes('street_number'))
    ) {
      if (
        place.address_components.some(
          ac => ac.long_name === 'Buenos Aires' || ac.short_name === 'CABA'
        ) ||
        validatePostalCode(place)
      ) {
        const neighborhood = place.address_components.find(ac =>
          ac.types.some(
            ac => ac === 'sublocality_level_1' || ac === 'neighborhood'
          )
        );
        const neighborhoodName = neighborhood?.long_name;
        const neighborhoodsIds = getNeighborhoods(
          neighborhoodName,
          neighborhoods
        );

        const number = place.address_components.find(ac =>
          ac.types.includes('street_number')
        )?.long_name;

        const street = place.address_components.find(ac =>
          ac.types.includes('route')
        )?.long_name;

        const cp = place.address_components.find(ac =>
          ac.types.includes('postal_code')
        )?.long_name;

        const cp_suffix = place.address_components.find(ac =>
          ac.types.includes('postal_code_suffix')
        )?.long_name;

        const area_level_1 = place.address_components.find(ac =>
          ac.types.includes('administrative_area_level_1')
        )?.short_name;

        const area_level_2 = place.address_components.find(ac =>
          ac.types.includes('administrative_area_level_2')
        )?.long_name;

        const locality = place.address_components.find(ac =>
          ac.types.includes('locality')
        )?.long_name;

        if (neighborhoodsIds) {
          setValues({
            number: parseInt(number) || '',
            street,
            barrios: neighborhoodsIds,
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
            cp,
            cp_suffix,
            area_level_1,
            area_level_2,
            locality,
            letter: ''
          });
          if (place.geometry && place.geometry.location) {
            setMobileGoogle(true);
          }
        }
      } else {
        setValues({
          number: '',
          street: '',
          barrios: [],
          lat: null,
          lng: null,
          cp: '',
          cp_suffix: '',
          area_level_1: '',
          area_level_2: '',
          locality: '',
          letter: ''
        });
        setMobileGoogle(false);

        messageHandler('error', 'La dirección ingresada no corresponde a CABA');
      }
    } else {
      messageHandler(
        'error',
        'La dirección ingresada no existe o no tiene altura asignada, verifique los datos e intente nuevamente.'
      );
    }
  };

  const checkData = string => {
    if (!string.trim()) {
      setEmptyInput(true);
      setValues({
        number: '',
        street: '',
        barrios: [],
        lat: null,
        lng: null,
        cp: '',
        cp_suffix: '',
        area_level_1: '',
        area_level_2: '',
        locality: '',
        letter: ''
      });
    } else {
      setEmptyInput(false);
    }
  };

  // init autocomplete
  const initAutocomplete = () => {
    if (!searchInput.current) return;
    var buenosAires = new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(-34.6202, -58.3737),
      new window.google.maps.LatLng(-34.5384, -58.4883),
      new window.google.maps.LatLng(-34.6597, -58.6038),
      new window.google.maps.LatLng(-34.4969, -58.4798),
      new window.google.maps.LatLng(-34.7338, -58.4322),
      new window.google.maps.LatLng(-34.5365, -58.2676),
      new window.google.maps.LatLng(-34.7161, -58.5403),
      new window.google.maps.LatLng(-34.5196, -58.3338)
    );
    const options = {
      types: ['address'],
      bounds: buenosAires,
      componentRestrictions: {
        country: 'ar'
      }
    };
    const autocomplete = new window.google.maps.places.Autocomplete(
      searchInput.current,
      options
    );
    autocomplete.setFields(['address_component', 'geometry']);
    autocomplete.addListener('place_changed', () =>
      onChangeAddress(autocomplete)
    );
  };

  const reverseGeocode = () => {
    const url = `https://maps.googleapis.com/maps/api/place/autocomplete/json?&location=37.76999%2C-122.44696&radius=500&key=${apiKey}'`;
    // const url = `${geocodeJson}?key=${apiKey}&latlng=${lat},${lng}`;
    searchInput.current.value = 'Getting your location...';
    fetch(url)
      .then(response => response.json())
      .then(location => {
        const place = location.results[0];
        const _address = extractAddress(place);
        searchInput.current.value = _address.plain();
      });
  };

  const findMyLocation = e => {
    e.preventDefault();

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        reverseGeocode(position.coords);
      });
    }
  };

  const validatePostalCode = place => {
    const min = 1000;
    const max = 1499;
    const hasPostalCode = place.address_components.some(component => {
      return component.types.includes('postal_code');
    });

    if (hasPostalCode) {
      const postalCodeComponent = place.address_components.find(component =>
        component.types.includes('postal_code')
      );
      const postalCode = postalCodeComponent.long_name;

      const postalCodeNumber = postalCode.replace(/\D/g, '');
      return (
        parseInt(postalCodeNumber) >= min && parseInt(postalCodeNumber) <= max
      );
    }
    return true;
  };
  // load map script after mounted
  useEffect(() => {
    if (ready) initAutocomplete();
  }, [ready]);

  return (
    <input
      className="form-control"
      ref={searchInput}
      type="text"
      defaultValue={inputValue}
      placeholder="Ingresá la calle y altura ..."
      onBlur={e => checkData(e.target.value)}
      onSubmit={findMyLocation}
    />
  );
};

InputAddressAutocomplete.propTypes = {
  ready: PropTypes.bool.isRequired,
  values: PropTypes.object.isRequired,
  setValues: PropTypes.func.isRequired,
  setEmptyInput: PropTypes.func.isRequired,
  neighborhoods: PropTypes.array.isRequired
};

export default InputAddressAutocomplete;
